import type { PopoverProps } from '@mui/material';
import { useContextMenu } from '@/common/components/ui/swimlane/context-menu';

import { type SwimlaneEvent, useSwimlaneEventPublisher } from '../../api';
import type { ICardProps, TCardId } from '../../types';
import { buildCardId, parseCardId } from '../../utils';

import {
  bindEventHandler,
  buildEventHandlerCssSelector,
  useEventDelegationHandler,
} from './useEventDelegationHandler';

// COULDDO: The most of the logic like this can be implemented in consumer components since we define slots there
export const useCardEventHandlers = () => {
  const { openContextMenu, closeContextMenu } = useContextMenu();
  const publishEvent = useSwimlaneEventPublisher();

  const onClickProps = useEventDelegationHandler('onClick', {
    openCardDetails: (event, _eventTarget, eventData) => {
      const cardId = eventData as TCardId;
      if (cardId) {
        publishEvent('openCardDetails', parseCardId(cardId), event as SwimlaneEvent);
      }
    },
    cardClickDisabled: () => undefined,
    cardMenuButton: (_event, eventTarget, eventData) => {
      const cardId = eventData as TCardId;

      if (!cardId) return;

      const isDeletable = eventTarget.getAttribute('data-item-is-deletable') === 'true';
      openContextMenu(cardId, eventTarget, { isDeletable });
    },
  });

  const onContextMenuProps = useEventDelegationHandler('onContextMenu', {
    cardContextMenu: (event, eventTarget, eventData) => {
      const cardId = eventData as TCardId;

      if (!cardId) return;

      event.preventDefault();
      const isDeletable = eventTarget.getAttribute('data-item-is-deletable') === 'true';
      const { clientX, clientY } = event as unknown as MouseEvent;
      const getBoundingClientRect = () => ({
        left: clientX,
        top: clientY,
        bottom: clientY,
        right: clientX,
        x: clientX,
        y: clientY,
        width: 0,
        height: 0,
      });
      openContextMenu(cardId, { getBoundingClientRect, nodeType: 1 } as PopoverProps['anchorEl'], {
        isDeletable,
      });
    },
    closeContextMenu,
  });

  return {
    ...onClickProps,
    ...bindCardClickDisabledHandler(),
    ...onContextMenuProps,
    ...bindEventHandler('onContextMenu', 'closeContextMenu'),
    onMouseLeave: closeContextMenu, // Prevents a misalignment of the menu when the mouse leaves the Swimlane
  };
};

export const bindOpenCardDetailsHandler = (props: ICardProps) =>
  bindEventHandler('onClick', 'openCardDetails', buildCardId(props));
export const openCardDetailsCssSelector = buildEventHandlerCssSelector(
  'onClick',
  'openCardDetails',
);

export const bindCardMenuButtonClickHandler = (props: ICardProps, isDeletable = false) => ({
  ...bindEventHandler('onClick', 'cardMenuButton', buildCardId(props)),
  'data-item-is-deletable': `${isDeletable}`,
});

export const bindCardClickDisabledHandler = () => bindEventHandler('onClick', 'cardClickDisabled');
export const cardClickDisabledCssSelector = buildEventHandlerCssSelector(
  'onClick',
  'cardClickDisabled',
);

export const bindCardContextMenuHandler = (props: ICardProps, isDeletable = false) => ({
  ...bindEventHandler('onContextMenu', 'cardContextMenu', buildCardId(props)),
  'data-item-is-deletable': `${isDeletable}`,
});
